import { useState, type FormEvent } from 'react';
import { FileText, Plus, Settings, Trash2, X, Upload, Sparkles, Loader2 } from 'lucide-react';
import { templateAPI, systemMediaAPI, type PromptTemplateDetail } from '../../services/api';
import toast from '../Toast';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

interface TemplatesTabProps {
  templates: (PromptTemplateDetail & { _count: { favorites: number; usages: number } })[];
  onLoadData: () => void;
}

export default function TemplatesTab({ templates, onLoadData }: TemplatesTabProps) {
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplateDetail | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这个模板吗？')) return;
    try {
      await templateAPI.deleteTemplate(id);
      onLoadData();
    } catch (error) {
      toast.error('删除失败');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">提示词模板管理</h2>
          <p className="text-sm text-light-muted">管理模板广场中的提示词模板</p>
        </div>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowForm(true);
          }}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-blue px-4 py-2 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          添加模板
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-light-hover">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">示例图</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">标题</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">分类</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">使用/收藏</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">操作</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="border-t border-light-border">
                  <td className="px-4 py-3">
                    {template.coverImage ? (
                      <img
                        src={resolveBrowserMediaUrl(template.coverImage)}
                        alt={template.title}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-light-hover flex items-center justify-center">
                        <FileText className="w-5 h-5 text-light-muted" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-light-text">{template.title}</p>
                      <p className="text-xs text-light-muted truncate max-w-xs line-clamp-1">{template.templateText || '无提示词'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded-lg text-xs bg-blue-100 text-blue-600">
                      {(template as any).category === 'general' && '通用'}
                      {(template as any).category === 'ecommerce' && '电商与产品'}
                      {(template as any).category === 'poster' && '品牌海报'}
                      {(template as any).category === 'typography' && '创意字体'}
                      {(template as any).category === 'infographic' && '知识科普'}
                      {(template as any).category === 'ui' && 'UI界面'}
                      {(template as any).category === 'photography' && '真实摄影'}
                      {(template as any).category === 'character' && '角色一致性'}
                      {(template as any).category === 'edit' && '图片编辑'}
                      {(template as any).category === 'art' && '艺术创作'}
                      {(template as any).category === 'portrait' && '人像'}
                      {(template as any).category === 'landscape' && '风景'}
                      {(template as any).category === 'anime' && '动漫'}
                      {(template as any).category === 'architecture' && '建筑'}
                      {(template as any).category === 'food' && '美食'}
                      {(template as any).category === 'other' && '其他'}
                      {!(template as any).category && '通用'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-light-text">
                      {template._count?.usages || 0} / {template._count?.favorites || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowForm(true);
                        }}
                        className="p-2 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="编辑"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-light-muted">
                    暂无模板，点击「添加模板」创建
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-light-border">
          {templates.map((template) => (
            <div key={template.id} className="p-4">
              <div className="flex gap-3">
                {/* Cover Image */}
                <div className="shrink-0">
                  {template.coverImage ? (
                    <img
                      src={resolveBrowserMediaUrl(template.coverImage)}
                      alt={template.title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-light-hover flex items-center justify-center">
                      <FileText className="w-5 h-5 text-light-muted" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-light-text truncate">{template.title}</p>
                      <p className="text-xs text-light-muted line-clamp-1 mt-0.5">{template.templateText || '无提示词'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-600">
                      {(template as any).category === 'general' && '通用'}
                      {(template as any).category === 'ecommerce' && '电商与产品'}
                      {(template as any).category === 'poster' && '品牌海报'}
                      {(template as any).category === 'typography' && '创意字体'}
                      {(template as any).category === 'infographic' && '知识科普'}
                      {(template as any).category === 'ui' && 'UI界面'}
                      {(template as any).category === 'photography' && '真实摄影'}
                      {(template as any).category === 'character' && '角色一致性'}
                      {(template as any).category === 'edit' && '图片编辑'}
                      {(template as any).category === 'art' && '艺术创作'}
                      {(template as any).category === 'portrait' && '人像'}
                      {(template as any).category === 'landscape' && '风景'}
                      {(template as any).category === 'anime' && '动漫'}
                      {(template as any).category === 'architecture' && '建筑'}
                      {(template as any).category === 'food' && '美食'}
                      {(template as any).category === 'other' && '其他'}
                      {!(template as any).category && '通用'}
                    </span>
                    <span className="text-xs text-light-muted">
                      使用 {template._count?.usages || 0} · 收藏 {template._count?.favorites || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setEditingTemplate(template);
                    setShowForm(true);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-accent-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  编辑
                </button>
                <button
                  onClick={() => handleDelete(template.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  删除
                </button>
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="px-4 py-8 text-center text-light-muted">
              暂无模板，点击「添加模板」创建
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-3 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="mt-4 max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:mt-0 sm:max-h-[90vh] sm:p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-light-text">
                {editingTemplate ? '编辑模板' : '添加模板'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-light-hover rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-light-muted" />
              </button>
            </div>
            <TemplateForm
              template={editingTemplate}
              onClose={() => setShowForm(false)}
              onSuccess={() => {
                setShowForm(false);
                onLoadData();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateForm({
  template,
  onClose,
  onSuccess
}: {
  template: PromptTemplateDetail | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    title: template?.title || '',
    category: (template as any)?.category || 'general',
    coverImage: template?.coverImage || '',
    templateText: template?.templateText || '',
  });
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const handleAutoFill = async () => {
    if (!form.templateText.trim()) {
      toast.error('请先输入提示词模板');
      return;
    }
    setAnalyzing(true);
    try {
      const result = await templateAPI.analyzeTemplate(form.templateText);
      setForm(prev => ({
        ...prev,
        title: result.title,
        category: result.category,
      }));
    } catch (error: any) {
      toast.error(error.message || '分析失败');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        title: form.title,
        category: form.category,
        coverImage: form.coverImage,
        templateText: form.templateText,
      };
      if (template) {
        await templateAPI.updateTemplate(template.id, data);
      } else {
        await templateAPI.createTemplate(data);
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || '保存失败');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('图片大小不能超过5MB');
      return;
    }

    setUploading(true);
    try {
      const result = await systemMediaAPI.upload(file, 'template_cover');
      setForm(prev => ({ ...prev, coverImage: result.storageKey }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* 标题和分类 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <label className="text-sm font-medium text-light-text">标题 *</label>
            <button
              type="button"
              onClick={handleAutoFill}
              disabled={analyzing || !form.templateText.trim()}
              className="flex items-center gap-1 text-xs text-accent-blue transition-colors hover:text-accent-blue/80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  分析中...
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3" />
                  AI自动填充
                </>
              )}
            </button>
          </div>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="输入模板标题"
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">分类</label>
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text"
          >
            <option value="general">通用</option>
            <option value="ecommerce">电商与产品</option>
            <option value="poster">品牌海报</option>
            <option value="typography">创意字体</option>
            <option value="infographic">知识科普</option>
            <option value="ui">UI界面</option>
            <option value="photography">真实摄影</option>
            <option value="character">角色一致性</option>
            <option value="edit">图片编辑</option>
            <option value="art">艺术创作</option>
            <option value="portrait">人像</option>
            <option value="landscape">风景</option>
            <option value="anime">动漫</option>
            <option value="architecture">建筑</option>
            <option value="food">美食</option>
            <option value="other">其他</option>
          </select>
        </div>
      </div>

      {/* 左右结构 */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* 左侧：示例图片 */}
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">示例图片</label>
          <div className="space-y-3">
            {form.coverImage ? (
              <div className="relative w-full rounded-xl overflow-hidden border border-light-border">
                <img src={resolveBrowserMediaUrl(form.coverImage)} alt="示例" className="w-full h-auto object-contain" />
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, coverImage: '' }))}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-light-border transition-colors hover:border-accent-blue hover:bg-blue-50/50">
                <div className="flex flex-col items-center justify-center px-4 py-6 text-center">
                  <Upload className="w-8 h-8 text-light-muted mb-2" />
                  <p className="text-sm text-light-muted">点击上传示例图片</p>
                  <p className="text-xs text-light-muted mt-1">支持 JPG、PNG，最大 5MB</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </div>

        {/* 右侧：提示词模板 */}
        <div>
          <label className="text-sm font-medium text-light-text mb-1.5 block">提示词模板 *</label>
          <textarea
            value={form.templateText}
            onChange={(e) => setForm({ ...form, templateText: e.target.value })}
            placeholder="输入提示词模板，用于指导大模型根据用户需求生成新的提示词。例如：

你是一位专业的AI绘画提示词工程师。用户想要生成{用户输入}风格的图片。

请根据用户的需求，生成一段高质量的英文AI绘画提示词，包含：
1. 主体详细描述
2. 艺术风格和媒介
3. 光线和色彩氛围
4. 构图和视角
5. 质量修饰词

直接输出优化后的英文提示词，不要添加解释。"
            rows={18}
            className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text resize-none text-sm"
            required
          />
          <p className="text-xs text-light-muted mt-1.5">
            此提示词将用于指导大模型，根据用户输入的需求生成专业的AI绘画提示词。系统将使用公共融合提示词模板来组合模板提示词和用户需求。
          </p>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2 text-light-muted transition-colors hover:text-light-text sm:w-auto"
        >
          取消
        </button>
        <button
          type="submit"
          className="w-full rounded-xl bg-accent-blue px-6 py-2 font-medium text-white transition-all hover:shadow-lg sm:w-auto"
        >
          保存
        </button>
      </div>
    </form>
  );
}
